import React from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import { getLayerStyle } from '../../animations/presets';
import type { LayerAnimation, LayerBorder, LayerPosition, LayerShadow } from '../../types';

interface LayerWrapperProps {
  position: LayerPosition;
  animation: LayerAnimation;
  opacity?: number;
  children: React.ReactNode;
  overflow?: 'hidden' | 'visible';
  /** CSS border applied to the layer wrapper */
  border?: LayerBorder;
  /** CSS border-radius in px */
  borderRadius?: number;
  /** CSS box-shadow */
  shadow?: LayerShadow;
}

/**
 * Positions a layer absolutely within the composition and applies
 * enter/exit animations from the layer's animation config.
 * Also applies optional border, borderRadius, and shadow decorations.
 */
export function LayerWrapper({
  position,
  animation,
  opacity = 1,
  children,
  overflow = 'hidden',
  border,
  borderRadius,
  shadow,
}: LayerWrapperProps) {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const animStyle = getLayerStyle(frame, fps, durationInFrames, animation, opacity);

  const borderCss = border && border.width > 0
    ? `${border.width}px ${border.style} ${border.color}`
    : undefined;

  const boxShadow = shadow
    ? `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`
    : undefined;

  const br = borderRadius != null ? borderRadius : undefined;

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        zIndex: position.zIndex,
        overflow,
        borderRadius: br,
        boxShadow,
        ...animStyle,
      }}
    >
      {children}

      {/* Border overlay — sits on top of content so the video/image fills 100% */}
      {borderCss && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: borderCss,
            borderRadius: br,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
      )}
    </div>
  );
}
